import { HttpErrorResponse } from '@angular/common/http';
import { HttpStatus } from './http-status.util';
import { normalizeApiErrors } from './api-errors.util';

/**
 * What the user is told when a request is refused because their role is not
 * allowed to make it.
 *
 * A role refusal arrives as a 403 with an EMPTY body, so there is nothing in
 * `errors` to show and the generic fallback would otherwise surface the raw
 * `Http failure response ... 403 Forbidden` string.
 *
 * A 403 means "wrong role", NOT "session gone" — it must never be routed into
 * the 401-only forced-logout path in `session-expiry.interceptor.ts`.
 */
export const FORBIDDEN_ERROR_MESSAGE =
  'You do not have permission to perform this action.';

/** Last-resort text when nothing else describes the failure. */
export const GENERIC_ERROR_MESSAGE = 'Unknown error occurred';

/**
 * Resolves the messages to show for a failed request, in priority order:
 *
 *   1. Messages supplied with the response body (`errors`).
 *   2. The role-refusal message for a 403, which arrives with an empty body
 *      and so supplies none.
 *   3. A generic fallback.
 *
 * `errors` is typed `string[]` but some routes answer with a bare string, so
 * every read goes through {@link normalizeApiErrors} — never a direct
 * `.length` or `Array.isArray` test (a non-empty string is truthy for the
 * former and false for the latter, and both bugs have been shipped before).
 *
 * Only the message *resolution* is shared. Each service still builds its own
 * response envelope, because their `data` shapes differ and those differences
 * are load-bearing for their consumers.
 *
 * Pure and DI-free by contract.
 *
 * @param error   the failed request.
 * @param options `fallbackMessage` overrides step 3's text — pass it when the
 *                service prefers a message carried in the response body over
 *                the transport-level one.
 */
export function resolveApiErrorMessages(
  error: HttpErrorResponse,
  options: { fallbackMessage?: string } = {}
): string[] {
  const supplied = normalizeApiErrors(error.error?.errors);
  if (supplied.length > 0) {
    return supplied;
  }

  if (error.status === HttpStatus.Forbidden) {
    return [FORBIDDEN_ERROR_MESSAGE];
  }

  const fallback = (options.fallbackMessage ?? error.message ?? '')
    .toString()
    .trim();

  return [fallback || GENERIC_ERROR_MESSAGE];
}
